'use client';

// Self-contained, dependency-free WebGL "band sweep" — a single soft
// luminous band that travels across the screen on a loop. Used as the
// visual for a deliberate punctuation moment (the network-switch overlay).
// MOR-607.
//
// Why in-house instead of glimm: glimm ships no license and is v0.1.0, so
// it can't be a production dependency. This renderer reproduces the
// documented glimm idea (cosine-palette band, Gaussian falloff, swept on a
// timeline) in ~one shader we control. GPU-composited: the band lives
// entirely in a fragment shader, so the cost is one full-screen draw per
// frame and nothing touches React between frames.
//
// Lifecycle: the GL context is created on mount and fully torn down on
// unmount (program, buffer, and the context itself via WEBGL_lose_context),
// so an overlay that closes leaves no lingering context. Callers gate this
// on reduced-motion — the component itself assumes motion is allowed.

import { useEffect, useRef } from 'react';

import { cn } from '@/lib/ui/utils';

export type SweepDirection = 'ltr' | 'rtl' | 'ttb' | 'btt';

interface ShaderSweepProps {
  /** Axis + sense the band travels along. Default `'ltr'`. */
  direction?: SweepDirection;
  /** Milliseconds for one full traversal (it then loops). Default 1100. */
  durationMs?: number;
  /** RGB scaling of the band. ~0.85 reads better on dark surfaces. Default 0.9. */
  brightness?: number;
  /**
   * Paint a dark backdrop each frame so the band is visible on any surface.
   * Default `false` — the overlay leaves it transparent so the dimmed app
   * shows through; a standalone preview wants `true`.
   */
  opaque?: boolean;
  className?: string;
}

// Cosine gradient: color(t) = a + b·cos(2π·(c·t + d)). The classic
// Inigo-Quilez rainbow, nudged toward a cool brand-leaning sweep.
const PAL_A: Float32Array = new Float32Array([0.5, 0.5, 0.55]);
const PAL_B: Float32Array = new Float32Array([0.5, 0.5, 0.5]);
const PAL_C: Float32Array = new Float32Array([1, 1, 1]);
const PAL_D: Float32Array = new Float32Array([0.55, 0.4, 0.75]);

const BAND_TIGHTNESS = 26; // Gaussian falloff: higher = narrower band.
const EDGE_WAVE = 1; // turbulence of the band's leading edge.
const MAX_DPR = 2;

const VERT_SRC = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `
precision highp float;
varying vec2 vUv;
uniform float uProgress;
uniform float uTime;
uniform float uTight;
uniform float uBrightness;
uniform float uWave;
uniform vec2 uDir;
uniform vec3 uPalA, uPalB, uPalC, uPalD;

vec3 palette(float t) {
  return uPalA + uPalB * cos(6.28318530718 * (uPalC * t + uPalD));
}

void main() {
  // Coordinate along the sweep axis, remapped to [0,1] for any direction.
  float p = dot(vUv - 0.5, uDir) + 0.5;
  // Perpendicular coordinate drives the wavy leading edge + shimmer.
  float crs = dot(vUv - 0.5, vec2(uDir.y, -uDir.x));
  // Band center travels from just off-screen to just off-screen.
  float center = mix(-0.25, 1.25, uProgress);
  float wave = sin(crs * 18.0 + uTime * 3.0) * 0.03 * uWave;
  float d = p - center + wave;
  float band = clamp(exp(-(d * d) * uTight), 0.0, 1.0);
  vec3 col = palette(p * 0.6 + uTime * 0.05) * uBrightness;
  gl_FragColor = vec4(col, band);
}`;

export function ShaderSweep({
  direction = 'ltr',
  durationMs = 1100,
  brightness = 0.9,
  opaque = false,
  className,
}: ShaderSweepProps): React.ReactNode {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;

    const gl = canvas.getContext('webgl', {
      alpha: true,
      premultipliedAlpha: false,
      // Keep the drawn frame in the buffer between paints. Without this some
      // compositors present an undefined (white/blank) buffer for a canvas
      // drawn outside the compositor's own frame callback.
      preserveDrawingBuffer: true,
    });
    if (gl === null) return; // No WebGL → render nothing; overlay still shows the dimmed backdrop.

    const program = buildProgram(gl);
    if (program === null) return;

    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    // Two triangles covering clip space.
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );

    // Bound to a non-`use*` name so Biome's hook heuristic doesn't mistake
    // the WebGL `useProgram` method for a React hook.
    const activateProgram = gl.useProgram.bind(gl);
    activateProgram(program);
    const aPos = gl.getAttribLocation(program, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    const u = (name: string): WebGLUniformLocation | null => gl.getUniformLocation(program, name);
    const uProgress = u('uProgress');
    const uTime = u('uTime');
    gl.uniform1f(u('uTight'), BAND_TIGHTNESS);
    gl.uniform1f(u('uBrightness'), brightness);
    gl.uniform1f(u('uWave'), EDGE_WAVE);
    gl.uniform2fv(u('uDir'), dirVector(direction));
    gl.uniform3fv(u('uPalA'), PAL_A);
    gl.uniform3fv(u('uPalB'), PAL_B);
    gl.uniform3fv(u('uPalC'), PAL_C);
    gl.uniform3fv(u('uPalD'), PAL_D);

    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // `preserveDrawingBuffer` keeps the last frame, so clear every frame to
    // avoid the band accumulating. Opaque preview clears to a dark backdrop;
    // the overlay clears to transparent so the dimmed app shows through.
    if (opaque) gl.clearColor(0.04, 0.04, 0.06, 1);
    else gl.clearColor(0, 0, 0, 0);

    let raf = 0;
    let start = 0;
    const frame = (now: number): void => {
      if (start === 0) start = now;
      const elapsed = now - start;
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform1f(uProgress, (elapsed % durationMs) / durationMs);
      gl.uniform1f(uTime, elapsed / 1000);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      raf = window.requestAnimationFrame(frame);
    };
    raf = window.requestAnimationFrame(frame);

    return (): void => {
      window.cancelAnimationFrame(raf);
      ro.disconnect();
      gl.deleteBuffer(quad);
      gl.deleteProgram(program);
      // NB: do NOT call WEBGL_lose_context.loseContext() here. React's dev
      // StrictMode runs setup→cleanup→setup on the same <canvas>; losing the
      // context in cleanup leaves the second setup drawing onto a dead context
      // (blank/white canvas). The context is released by GC when the canvas
      // unmounts, which is what we want.
    };
  }, [direction, durationMs, brightness, opaque]);

  // `aria-hidden` lives on the wrapper, not the <canvas>: a11y tooling treats
  // canvas as natively focusable and flags `aria-hidden` placed on it directly.
  return (
    <div aria-hidden className={cn('size-full', className)}>
      <canvas ref={canvasRef} className="block size-full" />
    </div>
  );
}

function dirVector(direction: SweepDirection): Float32Array {
  if (direction === 'rtl') return new Float32Array([-1, 0]);
  if (direction === 'ttb') return new Float32Array([0, 1]);
  if (direction === 'btt') return new Float32Array([0, -1]);
  return new Float32Array([1, 0]);
}

function buildProgram(gl: WebGLRenderingContext): WebGLProgram | null {
  const vert = compileShader(gl, gl.VERTEX_SHADER, VERT_SRC);
  const frag = compileShader(gl, gl.FRAGMENT_SHADER, FRAG_SRC);
  if (vert === null || frag === null) return null;

  const program = gl.createProgram();
  if (program === null) return null;
  gl.attachShader(program, vert);
  gl.attachShader(program, frag);
  gl.linkProgram(program);
  // Shaders are linked into the program; the standalone objects are no
  // longer needed and can be flagged for deletion immediately.
  gl.deleteShader(vert);
  gl.deleteShader(frag);
  if (gl.getProgramParameter(program, gl.LINK_STATUS) !== true) {
    gl.deleteProgram(program);
    return null;
  }
  return program;
}

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (shader === null) return null;
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (gl.getShaderParameter(shader, gl.COMPILE_STATUS) !== true) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}
